// @ts-nocheck
/*
 * engraving-new.js
 *
 * New engraving modal (V2) — banner-style design picker, relationship
 * templates (sourced from product metafields), custom editor, and live
 * curved-banner SVG preview.
 *
 * Public custom elements:
 *   <product-engraving-new>           — host element for the new modal
 *   <engraving-new-dialog-component>  — popup wrapper (wraps DialogComponent)
 *
 * Data flow:
 *   The Liquid template (snippets/engraving-text-design-knife.liquid) renders
 *   relationship tabs (.rel-btn) and per-relationship preset grids
 *   (#presetGrid-N > .preset-chip) directly from the
 *   product.metafields.custom.engraving_configuration metafield. Each chip
 *   carries data-blade / data-year / data-handle / data-left-icon /
 *   data-right-icon / data-left-ar / data-right-ar attributes — the JS just
 *   reads from them, so all preset content is editable in Shopify Admin.
 *   The "Swap icons" grid is rendered in Liquid from shop metaobjects
 *   (engraving_icon_library); JS only attaches click handlers.
 *
 * The element posts to /cart/add.js using the same payload shape as the
 * legacy engraving.js so cart/order workflows stay compatible — including
 * `_notes_json` (structured products → option keys → text / banner icons) and
 * `_engraving_note_id` for correlating line-item properties.
 *
 * Optional theme setting "Design mode": when enabled, renders `#engDesignHud`
 * markup in Liquid (first child under `<product-engraving-new>`), loads Fabric
 * from esm.sh onto the preview, horizontal blade resize → width %, editable
 * curve fields, HUD strings.
 */

import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from "@theme/dialog";
import { CartAddEvent, CartErrorEvent, ThemeEvents } from "@theme/events";
import { getAdvancedProductFormData, PULeatherSheathsPDP } from "@theme/utilities";
import {
  getCartDrawerSectionId,
  getCartItemCountFromSections,
  setCartDrawerLoadingState,
} from "@theme/cart-actions";

// =============================================================================
// BANNER_GEOMETRY — per-knife curve geometry. Defined as a quadratic bezier
// with three points: P0 (left endpoint), P1 (control / pulls toward blade
// edge), P2 (right end). Icons are positioned ON this curve, sized to match
// the font cap-height, rotated to match the curve's tangent at their position.
// =============================================================================
const BANNER_GEOMETRY = {
  gator: {
    svgW: 200,
    svgH: 56,
    P0: [4, 26],
    P1: [100, 42],
    P2: [196, 26],
  },
  rhino: { svgW: 200, svgH: 50, P0: [4, 26], P1: [100, 27.5], P2: [196, 24.5] },
  takoda: {
    svgW: 194,
    svgH: 43,
    P0: [4, 6],
    P1: [100.5, 51.5],
    P2: [196.5, 30.5],
  },
  whale: {"svgW":200,"svgH":41,"P0":[4,27],"P1":[100,30.5],"P2":[196,25.5]},
  makwa: { svgW: 191, svgH: 54, P0: [4, 35.5], P1: [100, 36.5], P2: [196, 28] },
  drako: { svgW: 200, svgH: 56, P0: [4, 21.5], P1: [100, 42], P2: [196, 26] },
  ronin: { svgW: 200, svgH: 46, P0: [4, 32], P1: [100, 31], P2: [196, 33.5] },
  kurai: {
    svgW: 200,
    svgH: 39,
    P0: [5, 13.5],
    P1: [100, 36],
    P2: [196, 17],
  },
  "fixed-blade": {
    svgW: 200,
    svgH: 56,
    P0: [4, 8],
    P1: [100, 47],
    P2: [196, 35.5],
  },
  "tungsten-xl-chef-knife": {
    svgW: 200,
    svgH: 42,
    P0: [4, 18],
    P1: [100, 42],
    P2: [198.5, 28.5],
  },
  "tungsten-fat": {
    svgW: 200,
    svgH: 52,
    P0: [4, 9.5],
    P1: [100, 42],
    P2: [196, 26],
  },
  "galba-butcher-knife": {
    svgW: 200,
    svgH: 38,
    P0: [4, 17.5],
    P1: [100, 53],
    P2: [196, 20],
  },
  "machado-butcher-knife": {
    svgW: 200,
    svgH: 46,
    P0: [4, 21],
    P1: [120.5, 43.5],
    P2: [195.5, 25],
  },
  "daozi-butcher-knife": {
    svgW: 200,
    svgH: 40,
    P0: [15, 25],
    P1: [100, 45],
    P2: [200, 37],
  },
  "chef-knife": {
    svgW: 205,
    svgH: 40,
    P0: [0, 27],
    P1: [100, 55],
    P2: [210, 33],
  },
  "promaja-chef-knife": {
    svgW: 200,
    svgH: 50,
    P0: [6, 24],
    P1: [96, 30],
    P2: [188, 23],
  },
  "lixy-cleaver": {
    svgW: 200,
    svgH: 40,
    P0: [0, 31],
    P1: [100, 55],
    P2: [200, 23],
  },

  shark: { svgW: 200, svgH: 35, P0: [4, 17], P1: [98, 24], P2: [188.5, 20] },
  "tungsten-slicing-knife": {
    svgW: 200,
    svgH: 40,
    P0: [0, 20],
    P1: [100, 45],
    P2: [200, 25],
  },
  zubo: { svgW: 200, svgH: 36, P0: [4, 31], P1: [100, 35.5], P2: [200, 34.5] },
  cobra: { svgW: 200, svgH: 43, P0: [4, 13.5], P1: [100, 44.5], P2: [196, 26] },
  falca: { svgW: 200, svgH: 50, P0: [4, 26], P1: [30.5, 36], P2: [196, 26] },
  "suratu-mini-cleaver":{"svgW":200,"svgH":42,"P0":[4,25],"P1":[100,45],"P2":[196,31.5]},
  "butcher-knife":{"svgW":200,"svgH":50,"P0":[4,27],"P1":[100,64],"P2":[196,26]},
  kodiak:{"svgW":200,"svgH":56,"P0":[0,35],"P1":[92.5,44.5],"P2":[196,25]}
};

// Aspect-ratio cap so wide icons don't dominate the line.
const ICON_AR_CAP = 1.6;
// Gap between text and adjacent icons, as a fraction of font size.
const ICON_GAP_RATIO = 0.45;
// Initial-estimate char width (refined dynamically via getComputedTextLength).
const CHAR_WIDTH_EST = 0.52;

function escapeHtml(s) {
  return String(s).replace(
    /[&<>"]/g,
    (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
      })[c],
  );
}

/**
 * Shopify `image_url` often returns protocol-relative `//cdn...` URLs. Those
 * load in <img> but are unreliable inside inline SVG <image href>, so icons
 * vanish on the banner preview until we force the document scheme (https:).
 */
function normalizeIconSrc(src) {
  const s = String(src || "").trim();
  if (!s) return "";
  if (s.startsWith("//") && typeof window !== "undefined" && window.location) {
    return `${window.location.protocol}${s}`;
  }
  return s;
}

/** Strip disallowed characters per `data-allowed-chars` (same as engraving.js). */
function regExpCheck({ regExp, value, input }) {
  if (!regExp) return;
  const allowedChars = new RegExp(`^[${regExp}]*$`);
  if (!allowedChars.test(value)) {
    input.value = value.replace(new RegExp(`[^${regExp}]`, "g"), "");
  }
}

function effectiveAR(ar) {
  return Math.min(ICON_AR_CAP, Number.isFinite(ar) && ar > 0 ? ar : 1);
}

function curveChord(g) {
  return Math.hypot(g.P2[0] - g.P0[0], g.P2[1] - g.P0[1]);
}

/** Deep-clone a banner geometry object for safe edits in design mode. */
function cloneBannerGeom(g) {
  if (!g || !Array.isArray(g.P0)) return null;
  return {
    svgW: Number(g.svgW) || 200,
    svgH: Number(g.svgH) || 56,
    P0: [Number(g.P0[0]) || 0, Number(g.P0[1]) || 0],
    P1: [Number(g.P1[0]) || 0, Number(g.P1[1]) || 0],
    P2: [Number(g.P2[0]) || 0, Number(g.P2[1]) || 0],
  };
}

function chooseFontSize(text, geom, leftAR, rightAR) {
  const MAX_FONT_SIZE = 16;
  const cl = curveChord(geom);
  const lar = leftAR > 0 ? effectiveAR(leftAR) : 0;
  const rar = rightAR > 0 ? effectiveAR(rightAR) : 0;
  const factor =
    lar + rar + 2 * ICON_GAP_RATIO + (text.length || 1) * CHAR_WIDTH_EST;
  return Math.max(8, Math.min(MAX_FONT_SIZE, cl / factor));
}

function buildKnifeIconBannerLabel(text, leftIconName, rightIconName) {
  const parts = [];
  if (leftIconName !== "") parts.push(`ICON-L-${leftIconName}`);
  parts.push(text);
  if (rightIconName !== "") parts.push(`ICON-R-${rightIconName}`);
  return parts.join(" ");
}

class ProductEngravingNew extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
    /** Theme editor toggle: draggable zone overlays + geometry HUD */
    this.designMode = false;
    /** Design mode only: replaces `BANNER_GEOMETRY[knifeKey]` for live preview. */
    this.bannerGeometryOverride = null;
  }

  connectedCallback() {
    if (this.initialized) return;
    // Defer the real init until the wrapping <engraving-new-dialog-component>
    // (or any ancestor dialog) actually opens — DOM inside the modal may not
    // be ready / measurable until then, and there's no reason to wire events
    // or render the banner while the modal is hidden.
    const dialogHost =
      this.closest("engraving-new-dialog-component") ??
      this.closest("dialog-component");

    if (dialogHost) {
      const onDialogOpen = () => {
        if (this.initialized) return;
        this.#bootstrap();
      };
      dialogHost.addEventListener(DialogOpenEvent.eventName, onDialogOpen);

      // If the dialog is already open at connect time (e.g. SSR/hydration
      // edge case), bootstrap immediately.
      const openDialogEl = dialogHost.querySelector?.("dialog[open]");
      if (openDialogEl) this.#bootstrap();
      return;
    }

    // No dialog wrapper — fall back to immediate init (legacy / inline usage).
    this.#bootstrap();
  }

  /**
   * Runs once, on the first `dialog:open` (or immediately when there's no
   * dialog wrapper). Captures dataset-derived config, resolves DOM refs, and
   * wires up all event listeners via `#init()`.
   */
  #bootstrap() {
    if (this.initialized) return;
    this.initialized = true;

    // Unique per-instance suffix. Used to scope SVG element IDs (notably the
    // banner curve <path>) so that <textPath href="#..."> resolves to THIS
    // modal's curve, not the first matching id in the document. Without
    // this, multiple modals on the same page share the first opened curve.
    this.instanceId = Math.random().toString(36).slice(2, 11);

    this.designMode = this.dataset.designMode === "true";

    this.basePrice = parseFloat(this.dataset.basePrice || "0") || 0;
    this.feeHandle = parseFloat(this.dataset.feeHandle || "15") || 0;
    this.feeBlade = parseFloat(this.dataset.feeBlade || "35") || 0;
    this.hasHandle = this.dataset.hasHandle !== "false";
    this.knifeKey = this.dataset.knife || "gator";
    this.handlePropertyName = this.dataset.handleProperty || "Engraving Handle";
    this.bladePropertyName = this.dataset.bladeProperty || "Engraving Blade";
    this.bannerPropertyName = this.dataset.bannerProperty || "Engraving Banner";
    this.defaultRel = this.dataset.defaultRel || "";

    if (!this.hasHandle) {
      this.feeHandle = 0;
    }

    this.root = document.querySelector("product-form-component");
    this.mainForm =
      this.root?.querySelector('form[action="/cart/add"]') ??
      this.closest('form[action="/cart/add"]') ??
      this.closest("form");
    if (document.querySelector("pre-order-variant-block")) {
      this.root = document.querySelector(
        "pre-order-variant-block .pre-order-variant__atc-wrap:not([hidden]) product-form-component",
      );
      this.mainForm =
        this.root?.querySelector('form[action="/cart/add"]') ?? this.mainForm;
    }
    this.parentForm = this.mainForm;
    this.inputElements = this.querySelectorAll("input[type='text']");
    /** Labels for cart notes on blade banner (preset chip alts / icon swap names). */
    this.knifeBannerMeta = { leftIconName: "icon", rightIconName: "icon" };

    this.mainProductId =
      this.dataset.productId ||
      this.mainForm?.querySelector('input[name="id"]')?.value ||
      this.querySelector('input[name="id"]')?.value ||
      null;

    this.#init();
  }

  /**
   * Wire the modal: discover preset grids, bind events, and apply the first
   * chip in the initially-active grid as the starting state.
   */
  #init() {
    const self = this;
    const root = this;

    // ----- DOM refs (root-scoped) -----
    const stageBanner = root.querySelector("#stageBanner");
    const stageHandle = root.querySelector("#stageHandleText");
    const phraseInput = root.querySelector("#phraseInput");
    const yearInput = root.querySelector("#yearInput");
    const handleInput = root.querySelector("#handleInput");
    const yearQuickpicks = root.querySelector("#yearQuickpicks");
    const charCounter = root.querySelector("#charCounter");
    const customizeBody = root.querySelector("#customizeBody");
    const customizeToggle = root.querySelector("#customizeToggle");
    const iconSwapToggle = root.querySelector("#iconSwapToggle");
    const iconSwapDrawer = root.querySelector("#iconSwapDrawer");
    const iconSwapGrid = root.querySelector("#iconSwapGrid");
    const propHandle = root.querySelector("#engNewPropHandle");
    const propBlade = root.querySelector("#engNewPropBlade");
    const propBanner = root.querySelector("#engNewPropBanner");
    const propRel = root.querySelector("#engNewPropRel");
    const propPreset = root.querySelector("#engNewPropPreset");
    const priceTotal = root.querySelector("#priceTotal");
    const priceBase = root.querySelector("#engNewPriceBase");
    const priceLineHandle = root.querySelector("#priceLineHandle");
    const priceLineBlade = root.querySelector("#priceLineBlade");
    const footerCartBlade = root.querySelector("#footerCartBlade");
    const footerCartHandle = root.querySelector("#footerCartHandle");
    const footerChangeBlade = root.querySelector("#footerChangeBlade");
    const footerChangeHandle = root.querySelector("#footerChangeHandle");
    const previewStage = root.querySelector("#previewStage");
    if (previewStage) {
      previewStage.dataset.knife = self.knifeKey;
    }

    root
      .querySelectorAll("input[type='text'][data-allowed-chars]")
      .forEach((input) => {
        input.addEventListener("input", () => {
          regExpCheck({
            regExp: input.dataset.allowedChars,
            value: input.value,
            input,
          });
        });
      });

    function emitDesignZoneRefresh() {
      if (!self.designMode || !previewStage) return;
      previewStage.dispatchEvent(
        new CustomEvent("eng:design-refresh-zones", { bubbles: false }),
      );
    }

    if (self.designMode && previewStage) {
      previewStage.addEventListener(
        "eng:design-re-render-banner",
        () => {
          renderBanner();
        },
        false,
      );
    }

    // ----- State (no hardcoded defaults — derived from the first chip below) -----
    const state = {
      rel: "",
      presetIdx: 0,
      presetTag: "",
      leftIcon: { src: "", ar: 1 },
      rightIcon: { src: "", ar: 1 },
      text: "",
      year: "",
      handle: "",
      swapSide: "left",
      customMode: false,
    };

    // -------------------------------------------------------------------------
    // Banner SVG rendering — positions text + flanking icons on the per-knife
    // bezier so they read as one continuous engraved line.
    // -------------------------------------------------------------------------
    function fullEngraveText() {
      const t = (state.text || "").trim().toLowerCase();
      const y = (state.year || "").trim().toLowerCase();
      if (t && y) return `${t} · ${y}`;
      return t || y;
    }

    function positionStageIcons(
      fontSize,
      leftIconW,
      rightIconW,
      textLenFallback,
    ) {
      if (!stageBanner) return;
      const svg = stageBanner.querySelector("svg");
      if (!svg) return;
      // Query by tag rather than by id — the banner curve uses a per-instance
      // id (#bannerCurve-<instanceId>) and is the only <path> in this SVG.
      const path = svg.querySelector("path");
      const textEl = svg.querySelector("#bannerText");
      const lG = svg.querySelector("#leftIconG");
      const rG = svg.querySelector("#rightIconG");
      if (!path || !textEl || !lG || !rG) return;
      if (typeof path.getTotalLength !== "function") return;

      let textLen = 0;
      const textPath = textEl.querySelector("textPath");
      for (const el of [textPath, textEl]) {
        if (!el || typeof el.getComputedTextLength !== "function") continue;
        try {
          const len = el.getComputedTextLength();
          if (Number.isFinite(len) && len > 0) {
            textLen = len;
            break;
          }
        } catch {
          /* text not laid out yet */
        }
      }
      if (!Number.isFinite(textLen) || textLen <= 0) {
        const fb = Number(textLenFallback);
        if (Number.isFinite(fb) && fb > 0) textLen = fb;
      }
      if (!textLen) return;

      const pathLen = path.getTotalLength();
      const textStartArc = (pathLen - textLen) / 2;
      const textEndArc = textStartArc + textLen;
      const gap = fontSize * ICON_GAP_RATIO;

      const leftArc = Math.max(0, textStartArc - gap - leftIconW / 2);
      const rightArc = Math.min(pathLen, textEndArc + gap + rightIconW / 2);

      const lp = path.getPointAtLength(leftArc);
      const rp = path.getPointAtLength(rightArc);
      const eps = 0.5;
      const lp2 = path.getPointAtLength(Math.min(pathLen, leftArc + eps));
      const rp2 = path.getPointAtLength(Math.max(0, rightArc - eps));
      const lAngle = (Math.atan2(lp2.y - lp.y, lp2.x - lp.x) * 180) / Math.PI;
      const rAngle = (Math.atan2(rp.y - rp2.y, rp.x - rp2.x) * 180) / Math.PI;

      lG.setAttribute(
        "transform",
        `translate(${lp.x.toFixed(2)} ${lp.y.toFixed(2)}) rotate(${lAngle.toFixed(2)})`,
      );
      rG.setAttribute(
        "transform",
        `translate(${rp.x.toFixed(2)} ${rp.y.toFixed(2)}) rotate(${rAngle.toFixed(2)})`,
      );
    }

    function renderBanner() {
      const text = fullEngraveText();
      const upper = text;
      const baseGeom = BANNER_GEOMETRY[self.knifeKey] || BANNER_GEOMETRY.gator;
      const geom =
        self.designMode && self.bannerGeometryOverride
          ? self.bannerGeometryOverride
          : baseGeom;
      const leftSrc = normalizeIconSrc(state.leftIcon.src || "");
      const rightSrc = normalizeIconSrc(state.rightIcon.src || "");
      const fontSize = chooseFontSize(
        upper,
        geom,
        leftSrc ? state.leftIcon.ar : 0,
        rightSrc ? state.rightIcon.ar : 0,
      );
      const lar = leftSrc ? effectiveAR(state.leftIcon.ar) : 0;
      const rar = rightSrc ? effectiveAR(state.rightIcon.ar) : 0;
      const leftIconW = fontSize * lar;
      const rightIconW = fontSize * rar;
      const iconH = fontSize;
      const curveD = `M ${geom.P0[0]},${geom.P0[1]} Q ${geom.P1[0]},${geom.P1[1]} ${geom.P2[0]},${geom.P2[1]}`;
      const textLenFallback = (upper.length || 1) * fontSize * CHAR_WIDTH_EST;

      // Per-instance curve id so each modal's <textPath> resolves to its own
      // <path>, not the first #bannerCurve in the document. (SVG `href`
      // resolution searches the whole document, unlike querySelector which
      // we scope to the local svg.)
      const curveId = `bannerCurve-${self.instanceId}`;

      if (stageBanner) {
        stageBanner.innerHTML = `
          <svg class="banner-svg" viewBox="0 0 ${geom.svgW} ${geom.svgH}"
               preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <path id="${curveId}" d="${curveD}" fill="none"/>
            </defs>
            <g id="leftIconG" transform="translate(0 0)">
              ${leftSrc ? `<image href="${leftSrc}" x="${(-leftIconW / 2).toFixed(2)}" y="${(-iconH + 3).toFixed(2)}" width="${leftIconW.toFixed(2)}" height="${iconH.toFixed(2)}" preserveAspectRatio="xMidYMid meet"/>` : ""}
            </g>
            <text id="bannerText" fill="#111" font-family="Alegreya SC, serif" font-weight="700"
                  font-size="${fontSize.toFixed(2)}" letter-spacing="0.4">
              <textPath href="#${curveId}" startOffset="50%" text-anchor="middle">${escapeHtml(upper)}</textPath>
            </text>
            <g id="rightIconG" transform="translate(0 0)">
              ${rightSrc ? `<image href="${rightSrc}" x="${(-rightIconW / 2).toFixed(2)}" y="${(-iconH + 3).toFixed(2)}" width="${rightIconW.toFixed(2)}" height="${iconH.toFixed(2)}" preserveAspectRatio="xMidYMid meet"/>` : ""}
            </g>
          </svg>
        `;

        // Three passes to cover every timing case (sync / rAF / fonts.ready).
        positionStageIcons(fontSize, leftIconW, rightIconW, textLenFallback);
        requestAnimationFrame(() =>
          positionStageIcons(fontSize, leftIconW, rightIconW, textLenFallback),
        );
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(() =>
            positionStageIcons(
              fontSize,
              leftIconW,
              rightIconW,
              textLenFallback,
            ),
          );
        }
      }

      if (stageHandle)
        stageHandle.textContent = self.hasHandle
          ? (state.handle || "").toLowerCase()
          : "";
      if (footerCartBlade) {
        footerCartBlade.textContent = fullEngraveText().toUpperCase() || "—";
      }
      if (footerCartHandle) {
        footerCartHandle.textContent = self.hasHandle
          ? (state.handle || "").toLowerCase() || "—"
          : "";
      }

      if (charCounter) {
        const len = text.length;
        charCounter.textContent = `${len} char${len === 1 ? "" : "s"} on blade · production max 17`;
        charCounter.classList.toggle("warn", len > 17);
      }

      // Hidden line-item-property fields — what Shopify cart receives.
      if (propHandle) {
        propHandle.value = self.hasHandle
          ? (state.handle || "").toLowerCase()
          : "";
      }
      if (propBlade) propBlade.value = text;
      if (propBanner) {
        propBanner.value = `${normalizeIconSrc(state.leftIcon.src || "")}|${normalizeIconSrc(state.rightIcon.src || "")}`;
      }
      if (propRel) propRel.value = state.rel || "";
      if (propPreset) {
        propPreset.value = state.customMode ? "custom" : state.presetTag || "";
      }

      updatePrice();
      requestAnimationFrame(() => emitDesignZoneRefresh());
    }

    function updatePrice() {
      const handleHas =
        self.hasHandle && (state.handle || "").trim().length > 0;
      const bladeHas = (fullEngraveText() || "").trim().length > 0;
      const fees =
        (handleHas ? self.feeHandle : 0) + (bladeHas ? self.feeBlade : 0);
      const total = self.basePrice + fees;
      if (priceBase) priceBase.textContent = `$${self.basePrice.toFixed(2)}`;
      if (priceTotal) priceTotal.textContent = `$${total.toFixed(2)}`;
      if (priceLineHandle) {
        priceLineHandle.classList.toggle("is-active", handleHas);
        priceLineHandle.classList.toggle("is-inactive", !handleHas);
      }
      if (priceLineBlade) {
        priceLineBlade.classList.toggle("is-active", bladeHas);
        priceLineBlade.classList.toggle("is-inactive", !bladeHas);
      }
    }

    this._refreshPriceDisplay = updatePrice;

    // -------------------------------------------------------------------------
    // Preset / relationship handling — all data lives in the DOM (rendered
    // server-side from product.metafields.custom.engraving_configuration).
    // -------------------------------------------------------------------------
    const grids = Array.from(root.querySelectorAll('[id^="presetGrid-"]'));

    function getActiveGrid() {
      return (
        root.querySelector('[id^="presetGrid-"].active') || grids[0] || null
      );
    }

    function highlightActiveChip() {
      const active = getActiveGrid();
      if (!active) return;
      Array.from(active.querySelectorAll(".preset-chip")).forEach((chip, i) => {
        const isActive = !state.customMode && i === state.presetIdx;
        chip.classList.toggle("active", isActive);
        chip.setAttribute("aria-checked", isActive ? "true" : "false");
      });
    }

    function applyChip(chip, idx) {
      if (!chip) return;
      const ds = chip.dataset || {};
      state.presetIdx = idx;
      state.presetTag = ds.tag || "";
      state.text = ds.blade || "";
      state.year = ds.year || "";
      state.handle = self.hasHandle ? ds.handle || "" : "";
      state.leftIcon = {
        src: normalizeIconSrc(ds.leftIcon || ""),
        ar: parseFloat(ds.leftAr) || 1,
      };
      state.rightIcon = {
        src: normalizeIconSrc(ds.rightIcon || ""),
        ar: parseFloat(ds.rightAr) || 1,
      };
      state.customMode = false;

      self.knifeBannerMeta.leftIconName =
        (ds.leftIconName || "").trim() || "icon";
      self.knifeBannerMeta.rightIconName =
        (ds.rightIconName || "").trim() || "icon";

      if (phraseInput) phraseInput.value = state.text;
      if (yearInput) yearInput.value = state.year;
      if (handleInput) handleInput.value = state.handle;

      renderBanner();
      highlightActiveChip();
    }

    function showGridForRelButton(relBtn) {
      const idx = relBtn.dataset.index;
      grids.forEach((g) => {
        const matches = g.id === `presetGrid-${idx}`;
        g.classList.toggle("active", matches);
        g.classList.toggle("hidden", !matches);
      });
    }

    function applyRelTemplate(rel) {
      const relBtn =
        root.querySelector(`.rel-btn[data-rel="${rel}"]`) ||
        root.querySelector(".rel-btn.active") ||
        root.querySelector(".rel-btn");
      if (!relBtn) return;

      state.rel = relBtn.dataset.rel || "";

      // Corporate mode is owned by snippets/engraving-corporate-gifts.liquid
      if (state.rel === "corporate") {
        root.querySelectorAll(".rel-btn").forEach((b) => {
          const isActive = b === relBtn;
          b.classList.toggle("active", isActive);
          b.setAttribute("aria-selected", isActive ? "true" : "false");
        });
        return;
      }

      root.querySelectorAll(".rel-btn").forEach((b) => {
        const isActive = b === relBtn;
        b.classList.toggle("active", isActive);
        b.setAttribute("aria-selected", isActive ? "true" : "false");
      });

      showGridForRelButton(relBtn);

      const activeGrid = getActiveGrid();
      const firstChip = activeGrid?.querySelector(".preset-chip");
      if (firstChip) {
        applyChip(firstChip, 0);
      } else {
        // No chips configured for this relationship — clear the preview.
        state.text = "";
        state.year = "";
        state.handle = "";
        state.leftIcon = { src: "", ar: 1 };
        state.rightIcon = { src: "", ar: 1 };
        renderBanner();
      }

      renderYearQuickpicks(relBtn);
    }

    // -------------------------------------------------------------------------
    // Year quickpicks — comma-separated labels on each .rel-btn
    // (data-year-quickpicks). Buyers can still type any value in the input.
    // -------------------------------------------------------------------------
    function yearQuickpickLabelsFromRelBtn(relBtn) {
      const raw = (relBtn?.dataset?.yearQuickpicks || "").trim();
      if (!raw) return null;
      const labels = raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      return labels.length ? labels : null;
    }

    function isYearQuickpickBlankLabel(label) {
      const s = String(label || "").trim();
      return !s || /^no\s*year$/i.test(s) || s === "— blank —";
    }

    function renderYearQuickpicks(relBtn) {
      if (!yearQuickpicks) return;
      yearQuickpicks.innerHTML = "";
      const fromAttr = yearQuickpickLabelsFromRelBtn(relBtn);
      const picks = fromAttr || /** @type {string[]} */ (["2026", "No year"]);
      picks.forEach((y) => {
        const b = document.createElement("button");
        b.type = "button";
        const isBlank = isYearQuickpickBlankLabel(y);
        b.textContent = isBlank ? "No year" : String(y).trim();
        b.dataset.year = isBlank ? "" : String(y).trim();
        b.addEventListener("click", () => {
          state.year = b.dataset.year;
          state.customMode = true;
          if (yearInput) yearInput.value = state.year;
          renderBanner();
          highlightActiveChip();
        });
        yearQuickpicks.appendChild(b);
      });
    }

    // -------------------------------------------------------------------------
    // Icon swap drawer — .icon-swap-btn nodes come from Liquid
    // (engraving_icon_library metaobject). Wire click handlers once; do not
    // replace innerHTML (avoids detaching nodes mid-click vs dialog outside-tap).
    // -------------------------------------------------------------------------
    let iconSwapGridWired = false;

    function wireIconSwapGrid() {
      if (!iconSwapGrid || iconSwapGridWired) return;
      iconSwapGrid.querySelectorAll(".icon-swap-btn").forEach((btn) => {
        btn.addEventListener("click", (event) => {
          event.stopPropagation();
          const isNoIcon = btn.dataset.noIcon === "true";
          let newIcon;
          let iconName;
          if (isNoIcon) {
            newIcon = { src: "", ar: 1 };
            iconName = "";
          } else {
            const src = normalizeIconSrc(btn.dataset.src || "");
            const ar = parseFloat(String(btn.dataset.ar || "1")) || 1;
            newIcon = { src, ar };
            iconName =
              (btn.getAttribute("data-icon-name") || "").trim() || "icon";
          }
          if (state.swapSide === "left") {
            state.leftIcon = newIcon;
            self.knifeBannerMeta.leftIconName = iconName;
          } else {
            state.rightIcon = newIcon;
            self.knifeBannerMeta.rightIconName = iconName;
          }
          state.customMode = true;
          renderBanner();
          updateActiveIconSwapBtn();
          highlightActiveChip();
        });
      });
      iconSwapGridWired = true;
      updateActiveIconSwapBtn();
    }

    function updateActiveIconSwapBtn() {
      if (!iconSwapGrid) return;
      const currentSide =
        state.swapSide === "left" ? state.leftIcon : state.rightIcon;
      const currentSrc = normalizeIconSrc(currentSide?.src || "");
      iconSwapGrid.querySelectorAll(".icon-swap-btn").forEach((btn) => {
        const isNoIcon = btn.dataset.noIcon === "true";
        const isActive = isNoIcon
          ? !currentSrc
          : normalizeIconSrc(btn.dataset.src || "") === currentSrc;
        btn.classList.toggle("active", isActive);
      });
    }

    // -------------------------------------------------------------------------
    // Event wiring
    // -------------------------------------------------------------------------
    root.querySelectorAll(".rel-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        applyRelTemplate(btn.dataset.rel);
      });
    });

    grids.forEach((grid) => {
      Array.from(grid.querySelectorAll(".preset-chip")).forEach((chip, idx) => {
        chip.addEventListener("click", () => {
          // Make sure this chip's parent grid is the active one (in case the
          // user clicked a chip from a hidden grid via keyboard navigation).
          const relBtn = root.querySelector(
            `.rel-btn[data-rel="${grid.dataset.rel || ""}"]`,
          );
          if (relBtn && !grid.classList.contains("active")) {
            applyRelTemplate(relBtn.dataset.rel);
          }
          applyChip(chip, idx);
        });
      });
    });

    phraseInput?.addEventListener("input", () => {
      state.text = phraseInput.value;
      state.customMode = true;
      renderBanner();
      highlightActiveChip();
    });
    yearInput?.addEventListener("input", () => {
      state.year = yearInput.value;
      state.customMode = true;
      renderBanner();
      highlightActiveChip();
    });
    handleInput?.addEventListener("input", () => {
      if (!self.hasHandle) return;
      state.handle = handleInput.value;
      state.customMode = true;
      renderBanner();
      highlightActiveChip();
    });

    function openCustomizePanel() {
      if (!customizeBody || !customizeToggle) return;
      customizeBody.classList.remove("hidden");
      customizeToggle.setAttribute("aria-expanded", "true");
    }

    function focusEngravingInput(input) {
      if (!input) return;
      openCustomizePanel();
      input.focus();
      input.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    customizeToggle?.addEventListener("click", () => {
      if (!customizeBody) return;
      const isOpen = customizeBody.classList.toggle("hidden") === false;
      customizeToggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
    });

    footerChangeBlade?.addEventListener("click", () => {
      focusEngravingInput(phraseInput);
    });
    footerChangeHandle?.addEventListener("click", () => {
      if (!self.hasHandle) return;
      focusEngravingInput(handleInput);
    });

    iconSwapToggle?.addEventListener("click", () => {
      if (!iconSwapDrawer) return;
      const isOpen = iconSwapDrawer.classList.toggle("hidden") === false;
      iconSwapToggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
      if (isOpen) wireIconSwapGrid();
    });

    root.querySelectorAll(".icon-swap-side-toggle button").forEach((btn) => {
      btn.addEventListener("click", () => {
        root.querySelectorAll(".icon-swap-side-toggle button").forEach((b) => {
          b.classList.remove("active");
          b.setAttribute("aria-selected", "false");
        });
        btn.classList.add("active");
        btn.setAttribute("aria-selected", "true");
        state.swapSide = btn.dataset.side;
        updateActiveIconSwapBtn();
      });
    });

    const closeBtn = root.querySelector(".engraving-new-modal-close");
    if (closeBtn) {
      closeBtn.addEventListener("click", (event) => {
        event.preventDefault();
        const dialog =
          self.closest("engraving-new-dialog-component") ??
          self.closest("dialog-component");
        dialog?.closeDialog?.();
      });
    }

    const atcBtn = root.querySelector("#engNewAddToCart");
    if (atcBtn) atcBtn.addEventListener("click", (e) => self.handleSubmit(e));

    // -------------------------------------------------------------------------
    // Init: apply the initially-active rel/preset (also builds year quickpicks).
    // -------------------------------------------------------------------------
    const initialRelBtn =
      root.querySelector(`.rel-btn[data-rel="${self.defaultRel}"]`) ||
      root.querySelector(".rel-btn.active") ||
      root.querySelector(".rel-btn");
    if (initialRelBtn) {
      applyRelTemplate(initialRelBtn.dataset.rel);
    } else {
      renderYearQuickpicks(null);
      // No metafield data at all — nothing to render. Just update price/UI.
      updatePrice();
    }

    if (self.designMode && previewStage) {
      void self.#mountDesignFabric(previewStage);
    }
  }

  /**
   * When `data-design-mode` is true (theme block setting): loads Fabric.js from
   * esm.sh, overlays draggable rects on `#previewStage` that sync `--x`,
   * `--y`, `--r`; blade overlay can scale horizontally → `width: %` on
   * `.zone-blade`. Markup lives in Liquid (`#engDesignHud` — first child of
   * `<product-engraving-new>` when design mode is on); this wires Fabric + HUD.
   */
  async #mountDesignFabric(previewStage) {
    const knifeKey = this.knifeKey;
    const component = this;
    const hud = /** @type {HTMLElement | null} */ (
      component.querySelector("#engDesignHud")
    );
    if (!hud) {
      console.warn(
        "product-engraving-new design mode: missing #engDesignHud (theme block design_mode / Liquid)",
      );
      return;
    }

    const zoneHandle = previewStage.querySelector(".zone-handle");
    const zoneBlade = previewStage.querySelector(".zone-blade");
    if (!zoneBlade) return;
    const hasHandleZone = Boolean(zoneHandle);

    function requestBannerRerender() {
      previewStage.dispatchEvent(
        new CustomEvent("eng:design-re-render-banner", { bubbles: false }),
      );
    }

    function presetGeom() {
      return cloneBannerGeom(
        BANNER_GEOMETRY[knifeKey] || BANNER_GEOMETRY.gator,
      );
    }

    function effectiveGeom() {
      return (
        (component.designMode && component.bannerGeometryOverride) ||
        presetGeom()
      );
    }

    let fabricMod;
    try {
      fabricMod = await import("https://esm.sh/fabric@6.5.1");
    } catch (err) {
      console.warn(
        "product-engraving-new design mode: failed to load fabric.js",
        err,
      );
      return;
    }

    const { Canvas, Rect } = fabricMod;
    if (!(Canvas && Rect)) {
      console.warn(
        "product-engraving-new design mode: fabric Canvas/Rect not exported",
      );
      return;
    }

    const rowHandle = hud.querySelector('[data-part="handle"]');
    const rowBlade = hud.querySelector('[data-part="blade"]');
    const geomPanel = hud.querySelector("[data-geom-panel]");
    if (!hasHandleZone && rowHandle) {
      rowHandle.hidden = true;
    }

    function readGeomFromInputs() {
      if (!geomPanel) return null;
      const num = (name) => {
        const el = geomPanel.querySelector(`[data-geom="${name}"]`);
        const v = parseFloat(String(el?.value || "").trim());
        return Number.isFinite(v) ? v : null;
      };
      const svgW = num("svgW");
      const svgH = num("svgH");
      const p0x = num("p0x");
      const p0y = num("p0y");
      const p1x = num("p1x");
      const p1y = num("p1y");
      const p2x = num("p2x");
      const p2y = num("p2y");
      if ([svgW, svgH, p0x, p0y, p1x, p1y, p2x, p2y].some((n) => n === null)) {
        return null;
      }
      return cloneBannerGeom({
        svgW,
        svgH,
        P0: [p0x, p0y],
        P1: [p1x, p1y],
        P2: [p2x, p2y],
      });
    }

    function fillGeomInputsFromEffective() {
      if (!geomPanel) return;
      const g = effectiveGeom();
      if (!g) return;
      const map = {
        svgW: g.svgW,
        svgH: g.svgH,
        p0x: g.P0[0],
        p0y: g.P0[1],
        p1x: g.P1[0],
        p1y: g.P1[1],
        p2x: g.P2[0],
        p2y: g.P2[1],
      };
      Object.keys(map).forEach((k) => {
        const el = geomPanel.querySelector(`[data-geom="${k}"]`);
        if (el) el.value = String(map[k]);
      });
    }

    let geomDebounce = 0;
    function applyGeomFromInputsDebounced() {
      window.clearTimeout(geomDebounce);
      geomDebounce = window.setTimeout(() => {
        const next = readGeomFromInputs();
        if (!next) return;
        component.bannerGeometryOverride = next;
        requestBannerRerender();
      }, 120);
    }

    if (geomPanel) {
      geomPanel.querySelectorAll("input[data-geom]").forEach((inp) => {
        inp.addEventListener("input", applyGeomFromInputsDebounced);
        inp.addEventListener("change", applyGeomFromInputsDebounced);
      });
      geomPanel
        .querySelector('[data-action="geom-reset"]')
        ?.addEventListener("click", () => {
          component.bannerGeometryOverride = null;
          fillGeomInputsFromEffective();
          requestBannerRerender();
        });
      geomPanel
        .querySelector('[data-action="geom-copy"]')
        ?.addEventListener("click", async () => {
          const txt = JSON.stringify(effectiveGeom());
          try {
            await navigator.clipboard.writeText(txt);
          } catch {
            console.info("Banner geometry JSON:", txt);
          }
        });
    }

    fillGeomInputsFromEffective();

    const host = document.createElement("div");
    host.className = "eng-design-fabric-host";
    previewStage.appendChild(host);

    const canvasEl = document.createElement("canvas");
    host.appendChild(canvasEl);

    const canvas = new Canvas(canvasEl, {
      selection: false,
      preserveObjectStacking: true,
    });

    const baseOpts = {
      fill: "rgba(255, 200, 80, 0.1)",
      strokeWidth: 2,
      originX: "center",
      originY: "center",
      lockScalingFlip: true,
      hasControls: true,
      transparentCorners: false,
      cornerColor: "rgba(255,255,255,0.85)",
      cornerStrokeColor: "rgba(0,0,0,0.45)",
      borderColor: "rgba(255, 200, 80, 0.9)",
    };

    const rectHandle = hasHandleZone
      ? new Rect({
          ...baseOpts,
          lockScalingX: true,
          lockScalingY: true,
          stroke: "rgba(140, 220, 255, 0.95)",
          fill: "rgba(140, 220, 255, 0.1)",
        })
      : null;

    const rectBlade = new Rect({
      ...baseOpts,
      lockScalingX: false,
      lockScalingY: true,
      stroke: "rgba(255, 200, 80, 0.95)",
    });

    if (typeof rectBlade.setControlsVisibility === "function") {
      rectBlade.setControlsVisibility({
        tl: false,
        tr: false,
        bl: false,
        br: false,
        mt: false,
        mb: false,
        ml: true,
        mr: true,
        mtr: true,
      });
    }

    if (rectHandle) {
      /** @type {{ engZone?: 'handle' | 'blade' }} */
      rectHandle.engZone = "handle";
      canvas.add(rectHandle);
    }
    /** @type {{ engZone?: 'handle' | 'blade' }} */
    rectBlade.engZone = "blade";

    canvas.add(rectBlade);

    /** @param {HTMLElement} zoneEl */
    function readZoneCss(zoneEl) {
      const st = getComputedStyle(zoneEl);
      const xs = st.getPropertyValue("--x").trim();
      const ys = st.getPropertyValue("--y").trim();
      const rs = st.getPropertyValue("--r").trim();
      const xp = parseFloat(xs) || 0;
      const yp = parseFloat(ys) || 0;
      const rm = rs.match(/(-?[\d.]+)/);
      const rot = rm ? parseFloat(rm[1]) : 0;
      return { xp, yp, rot };
    }

    /** Sync CSS vars (+ optional blade width %) from Fabric. Width is applied on `modified` only. */
    function writeZoneFromFabric(zoneEl, obj, stageW, stageH, applyBladeWidth) {
      const xp = (obj.left / stageW) * 100;
      const yp = (obj.top / stageH) * 100;
      const rot =
        typeof obj.angle === "number" && Number.isFinite(obj.angle)
          ? obj.angle
          : 0;
      zoneEl.style.setProperty("--x", `${xp.toFixed(2)}%`);
      zoneEl.style.setProperty("--y", `${yp.toFixed(2)}%`);
      zoneEl.style.setProperty("--r", `${rot.toFixed(1)}deg`);

      if (!applyBladeWidth || obj.engZone !== "blade") return;

      const sx = obj.scaleX || 1;
      const scaledW =
        typeof obj.getScaledWidth === "function"
          ? obj.getScaledWidth()
          : (obj.width || 0) * sx;
      let wPct = (scaledW / stageW) * 100;
      wPct = Math.min(98, Math.max(5, wPct));
      zoneBlade.style.width = `${wPct.toFixed(2)}%`;
      if (Math.abs(sx - 1) > 0.002 || Math.abs((obj.scaleY || 1) - 1) > 0.002) {
        obj.set({ scaleX: 1, scaleY: 1 });
      }
      const bw = Math.max(24, zoneBlade.offsetWidth);
      const bh = Math.max(20, zoneBlade.offsetHeight);
      obj.set({ width: bw, height: bh });
      if (typeof obj.setCoords === "function") obj.setCoords();
    }

    /** @param {*} rect */
    function syncRectFromZone(zoneEl, rect, stageW, stageH) {
      const { xp, yp, rot } = readZoneCss(zoneEl);
      const bw = Math.max(24, zoneEl.offsetWidth);
      const bh = Math.max(20, zoneEl.offsetHeight);
      rect.set({
        left: (xp / 100) * stageW,
        top: (yp / 100) * stageH,
        angle: rot,
        width: bw,
        height: bh,
        scaleX: 1,
        scaleY: 1,
      });
      if (typeof rect.setCoords === "function") rect.setCoords();
    }

    function hudGeometry() {
      const g = effectiveGeom();
      return { chord: curveChord(g), json: JSON.stringify(g) };
    }

    function bladeWidthPct() {
      const sw = previewStage.clientWidth;
      if (sw < 2) return 0;
      return (zoneBlade.offsetWidth / sw) * 100;
    }

    function paintHud() {
      if (hasHandleZone && zoneHandle && rowHandle) {
        const h = readZoneCss(zoneHandle);
        rowHandle.textContent = `Handle zone — x: ${h.xp.toFixed(2)}%  y: ${h.yp.toFixed(2)}%  angle (deg): ${h.rot.toFixed(1)}`;
      }
      const b = readZoneCss(zoneBlade);
      const bw = bladeWidthPct();
      const { chord, json } = hudGeometry();
      if (rowBlade) {
        rowBlade.textContent =
          `Blade zone — x: ${b.xp.toFixed(2)}%  y: ${b.yp.toFixed(2)}%  angle (deg): ${b.rot.toFixed(1)}  width: ${bw.toFixed(2)}%  | ` +
          `curveChord: ${chord.toFixed(2)} (SVG units)  |  geometry: ${json}`;
      }
    }

    /** @param {*} e */
    function onObjectTransform(e, applyBladeWidth) {
      const target = e.target;
      if (!target || !target.engZone) return;
      const sw = previewStage.clientWidth;
      const sh = previewStage.clientHeight;
      if (sw < 2 || sh < 2) return;
      const zone =
        target.engZone === "handle" && zoneHandle ? zoneHandle : zoneBlade;
      if (!zone) return;
      writeZoneFromFabric(zone, target, sw, sh, applyBladeWidth);
      paintHud();
      if (typeof canvas.renderAll === "function") canvas.renderAll();
    }

    canvas.on("object:moving", (ev) => onObjectTransform(ev, false));
    canvas.on("object:rotating", (ev) => onObjectTransform(ev, false));
    canvas.on("object:modified", (ev) => onObjectTransform(ev, true));

    function layoutFabric() {
      const sw = previewStage.clientWidth;
      const sh = previewStage.clientHeight;
      if (sw < 2 || sh < 2) return;
      canvas.setDimensions({ width: sw, height: sh });
      if (hasHandleZone && zoneHandle && rectHandle) {
        syncRectFromZone(zoneHandle, rectHandle, sw, sh);
      }
      syncRectFromZone(zoneBlade, rectBlade, sw, sh);
      paintHud();
      if (typeof canvas.renderAll === "function") canvas.renderAll();
    }

    let roRaf = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(roRaf);
      roRaf = requestAnimationFrame(layoutFabric);
    });
    ro.observe(previewStage);

    requestAnimationFrame(layoutFabric);

    previewStage.addEventListener(
      "eng:design-refresh-zones",
      () => layoutFabric(),
      false,
    );
  }
  getDesignSelectedValue(productId) {
    const selectOption = this.querySelector(
      `select[data-product-id="${productId}"]`,
    ).selectedOptions[0];
    const sku = selectOption.value;
    const optionTitle = selectOption.innerText.trim();
    const label = selectOption.dataset.label;
    return { sku, label, optionTitle };
  }

  /**
   * Maps `{SKU}-{optionKey}` notes (or bare option keys) to JSON keys like `B`, `H`.
   */
  #optionKeyFromEngravingNote(note, sku) {
    const n = String(note || "");
    const sk = String(sku || "").trim();
    if (sk && n.startsWith(`${sk}-`)) return n.slice(sk.length + 1);
    return n || null;
  }

  /** Blade / banner line item: metafield option key `B`, exposed as `data-engraving-option-key`. */
  #isBladeEngravingInput(input) {
    if (!(input instanceof HTMLElement)) return false;
    const key = String(input.dataset.engravingOptionKey || "")
      .trim()
      .toLowerCase();
    if (key === "B") return true;
    const note = String(input.dataset.note || "");
    return note.endsWith("-B") || note === "B";
  }

  setEngravingProperty({ inputFieled, mainQuantity }) {
    const notes = [];
    const engravingIds = [];
    const engravingLabel = {};
    const notesJson = { products: {} };
    const leftIconName = this.knifeBannerMeta?.leftIconName ?? "icon";
    const rightIconName = this.knifeBannerMeta?.rightIconName ?? "icon";

    const bannerHidden = this.querySelector("#engNewPropBanner");
    let bannerLeft = "";
    let bannerRight = "";
    const rawBanner = bannerHidden?.value;
    if (rawBanner) {
      const parts = String(rawBanner).split("|");
      bannerLeft = (parts[0] || "").trim();
      bannerRight = (parts[1] || "").trim();
    }

    const ensureProductEntry = (sku) => {
      const s = String(sku || "").trim();
      if (!s) return null;
      if (!notesJson.products[s]) notesJson.products[s] = {};
      return notesJson.products[s];
    };

    inputFieled.forEach((input) => {
      if (input.dataset.option) {
        return;
      }
      const engravingType = input.dataset.engravingType;
      const productId = input.dataset.productId;
      const engravingId = input.dataset.engravingId;
      const skuNote = input.dataset.note || "";
      const engravingText = input.value;
      const label = input.dataset.label;
      const engravingPrice = parseFloat(input.dataset.engravingPrice);

      if (!engravingType || !engravingId || Number.isNaN(engravingPrice)) {
        return;
      }

      engravingIds.push(`${engravingId}:1:${engravingPrice}`);

      if (engravingType === "style") {
        const {
          sku,
          label: styleLabel,
          optionTitle,
        } = this.getDesignSelectedValue(productId);
        const combineNote = skuNote
          ? `${sku}-${skuNote}: ${engravingText}`
          : `${sku}: ${engravingText}`;
        notes.push(combineNote);
        if (styleLabel) engravingLabel[styleLabel] = optionTitle;
        return;
      }

      if (engravingType === "text") {
        const productHandle = input.dataset.productHandle;
        const yearVal = input.dataset.isYear
          ? (this.querySelector("#yearInput")?.value || "").trim()
          : "";
        const phrase = String(engravingText || "").trim();

        let combineNote = `${skuNote}: ${phrase || yearVal}`;
        let labelValue = phrase || yearVal;
        let labelNoteValue = phrase || yearVal;
        if (input.dataset.isIcon) {
          if (phrase && yearVal) {
            labelNoteValue = `${phrase} · ${yearVal}`;
            const bannerText = buildKnifeIconBannerLabel(
              labelNoteValue,
              leftIconName,
              rightIconName,
            );
            combineNote = `${skuNote}: ${bannerText}`;
            labelValue = bannerText;
          } else if (yearVal) {
            labelNoteValue = yearVal;
            const bannerText = buildKnifeIconBannerLabel(
              yearVal,
              leftIconName,
              rightIconName,
            );
            combineNote = `${skuNote}: ${bannerText}`;
            labelValue = bannerText;
          } else {
            const bannerText = buildKnifeIconBannerLabel(
              phrase,
              leftIconName,
              rightIconName,
            );

            combineNote = `${skuNote}: ${bannerText}`;
            labelValue = bannerText;
            labelNoteValue = phrase;
          }
        }
        notes.push(combineNote);

        if (label) {
          engravingLabel[label] = labelValue;
        }

        const skuForJson = (input.dataset.sku || "").trim();
        const jsonKey = this.#optionKeyFromEngravingNote(skuNote, skuForJson);
        const entry = ensureProductEntry(skuForJson);
        if (entry && jsonKey) {
          if (input.dataset.isIcon === "true") {
            entry[jsonKey] = {
              text: labelNoteValue,
              leftIcon: bannerLeft,
              rightIcon: bannerRight,
              key: "B-ICON",
            };
          } else {
            entry[jsonKey] = {
              text: engravingText,
            };
          }
        }
      }
    });
    return { notes, engravingIds, engravingLabel, notesJson };
  }

  setProductProperty({
    notes,
    engravingLabel,
    mainQuantity,
    engravingIds,
    notesJson,
  }) {
    const properties = {};
    if (!this.mainForm?.dataset.lpForm) {
      const mainFormData = new FormData(this.mainForm);
      for (const [key, value] of mainFormData.entries()) {
        const property = key.match(/^properties\[(.+)\]$/);
        if (property && value.trim().length > 0) {
          const propName = property[1];
          if (
            !this.hasHandle &&
            propName === this.handlePropertyName
          ) {
            continue;
          }
          properties[propName] = value.trim();
        }
      }
    }

    const nj =
      notesJson && typeof notesJson === "object" ? notesJson : { products: {} };
    const noteId = Date.now().toString(36);
    nj.noteId = noteId;

    return {
      id: this.mainProductId,
      quantity: mainQuantity,
      properties: {
        ...properties,
        ...engravingLabel,
        _engraving_ids: engravingIds.join(","),
        _notes: notes.join(","),
        _notes_json: JSON.stringify(nj),
        _engraving_note_id: noteId,
      },
    };
  }

  /**
   * Keep cart target and footer total in sync when buy-now vs pre-order toggles
   * or the variant picker changes on a product with pre-order-variant-block.
   * @param {{ id: string; priceCents: number }} active
   */
  syncFromPreOrderVariant(active) {
    if (!active?.id) return;

    const dollars = (Number(active.priceCents) || 0) / 100;
    this.dataset.productId = String(active.id);
    this.dataset.basePrice = String(dollars);
    this.inputElements = this.querySelectorAll("input[type='text']");
    if (active.sku) {
      this.inputElements.forEach((input) => {
        if (!input.dataset.sku) return;
        const oldSku = input.dataset.sku;
        if (oldSku === active.sku) return;
        input.dataset.sku = active.sku;
        input.dataset.productId = String(active.id);
        const note = input.dataset.note || "";
        if (note.startsWith(`${oldSku}-`)) {
          input.dataset.note = `${active.sku}${note.slice(oldSku.length)}`;
        }
      });
    }

    const priceBaseEl = this.querySelector("#engNewPriceBase");
    if (priceBaseEl) priceBaseEl.textContent = `$${dollars.toFixed(2)}`;

    if (!this.initialized) return;

    this.mainProductId = String(active.id);
    this.basePrice = dollars;

    if (document.querySelector("pre-order-variant-block")) {
      this.root = document.querySelector(
        "pre-order-variant-block .pre-order-variant__atc-wrap:not([hidden]) product-form-component",
      );
      this.mainForm =
        this.root?.querySelector('form[action="/cart/add"]') ?? this.mainForm;
      this.parentForm = this.mainForm;
    }

    this._refreshPriceDisplay?.();
  }

  /**
   * Submit the engraving order to Shopify cart using the same payload shape
   * as engraving.js — line-item properties carry the engraving text, the
   * banner icon pair, relationship/preset metadata, `_notes_json`, and
   * `_engraving_note_id` for downstream engraving workflows.
   */
  async handleSubmit(event) {
    event?.preventDefault?.();
    if (!this.mainProductId) {
      console.warn("product-engraving-new: missing product id");
      return;
    }

    const atcBtn = this.querySelector("#engNewAddToCart");
    if (atcBtn) {
      atcBtn.classList.add("loading");
      atcBtn.setAttribute("disabled", "disabled");
    }
    setCartDrawerLoadingState(true);

    const mainQuantity =
      this.dataset.cartMode === "replace"
        ? parseInt(this.dataset.cartQuantity || "1", 10)
        : parseInt(
            this.mainForm?.querySelector('input[name="quantity"]')?.value || 1,
            10,
          );
    const yearForBlade = (this.querySelector("#yearInput")?.value || "").trim();
    const inputFieled = Array.from(this.inputElements).filter((input) => {
      if (input.dataset.option) return false;
      // No handle engraving option configured — never add handle line items.
      if (!this.hasHandle) {
        const note = String(input.dataset.note || "");
        const key = String(input.dataset.engravingOptionKey || "")
          .trim()
          .toUpperCase();
        if (
          input.id === "handleInput" ||
          key === "H" ||
          note.endsWith("-H") ||
          note === "H"
        ) {
          return false;
        }
      }
      const v = input.value.trim();
      if (v !== "") return true;
      if (this.#isBladeEngravingInput(input) && yearForBlade !== "") {
        return true;
      }
      return false;
    });
    const { notes, engravingIds, engravingLabel, notesJson } =
      this.setEngravingProperty({
        inputFieled,
        mainQuantity,
      });
    const productData = this.setProductProperty({
      notes,
      engravingLabel,
      mainQuantity,
      engravingIds,
      notesJson,
    });

    let sections = "";
    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component",
    );
    const sectionIds = [];
    cartItemsComponents.forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sectionIds.push(el.dataset.sectionId);
      }
    });
    sections = sectionIds.join(",");
    const drawerSections = getCartDrawerSectionId();
    if (drawerSections && !sectionIds.includes(drawerSections)) {
      sections = sections ? `${sections},${drawerSections}` : drawerSections;
    }

    const isCartReplace =
      this.dataset.cartMode === "replace" && this.dataset.cartLineKey;

    try {
      const data = isCartReplace
        ? await this.#replaceCartLineAndAdd(productData, sections)
        : await this.#addEngravingToCart(productData, sections);

      if (data && !data.status) {
        // `cart-icon.js` only treats 'product-form-component' as additive, so this
        // event's itemCount is read as the cart total. Take it from the rendered
        // drawer section rather than from mainQuantity, which is just how many
        // items this submit added - and in replace mode a line was removed too.
        const cartItemCount = getCartItemCountFromSections(data.sections);

        this.dispatchEvent(
          new CartAddEvent({}, this.mainProductId, {
            source: "product-engraving-new",
            itemCount: cartItemCount ?? mainQuantity,
            productId: this.mainProductId,
            sections: data.sections,
          }),
        );
        const dialogContent =
          this.closest("engraving-new-dialog-component") ??
          this.closest("dialog-component");
        dialogContent?.closeDialog?.();
      } else if (data?.status) {
        console.error("Engraving cart add error:", data);
        this.dispatchEvent(
          new CartErrorEvent(
            this.mainForm ? this.mainForm.getAttribute("id") : "",
            data.message,
            data.description,
            data.errors,
          ),
        );
      }
    } catch (error) {
      console.error("Engraving fetch error:", error);
      if (error?.message) {
        this.dispatchEvent(
          new CartErrorEvent(
            this.mainForm ? this.mainForm.getAttribute("id") : "",
            error.message,
            error.description,
            error.errors,
          ),
        );
      }
    } finally {
      setCartDrawerLoadingState(false);
      if (atcBtn) {
        atcBtn.classList.remove("loading");
        atcBtn.removeAttribute("disabled");
      }
    }
  }

  /**
   * @param {object} productData
   * @param {string} sections
   */
  async #addEngravingToCart(productData, sections) {
    const bumpItems = this.dataset.cartMode === "replace" ? [] : getAdvancedProductFormData();

    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: [productData, ...bumpItems, ...PULeatherSheathsPDP(productData)],
        sections,
      }),
    });

    return response.json();
  }

  /**
   * Remove the plain cart line, then add the engraved replacement.
   * @param {object} productData
   * @param {string} sections
   */
  async #replaceCartLineAndAdd(productData, sections) {
    const lineKey = this.dataset.cartLineKey;
    if (!lineKey) {
      throw new Error("Missing cart line key for engraving replace");
    }

    /** @type {Record<string, string>} */
    const keptProperties = {};
    try {
      const cartResponse = await fetch("/cart.js");
      const cart = await cartResponse.json();
      const line = cart.items?.find((item) => item.key === lineKey);
      const current = line?.properties;
      if (current && typeof current === "object") {
        Object.entries(current).forEach(([key, value]) => {
          if (value == null || value === "") return;
          keptProperties[key] = String(value);
        });
      }
    } catch {
      // Engraving still applies; the line just won't keep prior properties.
    }

    const engravedLine = {
      ...productData,
      properties: {
        ...keptProperties,
        ...productData.properties,
      },
    };

    const removeFormData = new FormData();
    removeFormData.append(`updates[${lineKey}]`, "0");

    const removeResponse = await fetch("/cart/update.js", {
      method: "POST",
      body: removeFormData,
    });
    const removeData = await removeResponse.json();
    if (removeData.status) {
      return removeData;
    }

    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: [engravedLine],
        sections,
      }),
    });
    return response.json();
  }
}

if (!customElements.get("product-engraving-new")) {
  customElements.define("product-engraving-new", ProductEngravingNew);
}

class EngravingNewDialogComponent extends DialogComponent {
  #homeParent = null;
  #homeNext = null;

  /**
   * Stay out of the top layer so Google reCAPTCHA v2's body-level
   * image-challenge (z-index 2000000000) can appear above this dialog.
   */
  get usesTopLayer() {
    return false;
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(DialogCloseEvent.eventName, this.#restoreHome);
  }

  disconnectedCallback() {
    this.removeEventListener(DialogCloseEvent.eventName, this.#restoreHome);
    super.disconnectedCallback();
  }

  #restoreHome = () => {
    this.classList.remove("eng-dialog-open");
    document.documentElement.removeAttribute("scroll-lock");
    if (this.#homeParent?.isConnected && this.parentElement === document.body) {
      this.#homeParent.insertBefore(this, this.#homeNext);
    }
    this.#homeParent = null;
    this.#homeNext = null;
  };

  async showDialog() {
    if (this.parentElement !== document.body) {
      this.#homeParent = this.parentElement;
      this.#homeNext = this.nextSibling;
      document.body.appendChild(this);
    }
    this.classList.add("eng-dialog-open");
    document.documentElement.setAttribute("scroll-lock", "");

    if (this.classList.contains("cart-engraving-dialog-host")) {
      const cartDrawer = document.querySelector("cart-drawer-component");
      if (cartDrawer && typeof cartDrawer.closeDialog === "function") {
        await cartDrawer.closeDialog();
      }
    }

    super.showDialog();
  }
}

if (!customElements.get("engraving-new-dialog-component")) {
  customElements.define(
    "engraving-new-dialog-component",
    EngravingNewDialogComponent,
  );
}

document.addEventListener("click", (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const dialogClose = target.closest(
    ".engraving-new-link__content .popup-link__close",
  );
  if (dialogClose) {
    event.preventDefault();
  }

  const trigger = target.closest("[data-cart-engraving-trigger]");
  if (!trigger) return;

  event.preventDefault();
  const dialogId = trigger.getAttribute("data-cart-engraving-trigger");
  if (!dialogId) return;

  const host = document.getElementById(dialogId);
  if (host && typeof host.showDialog === "function") {
    host.showDialog();
  }
});
